import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, Input, Link, Text, Textarea } from '@siteground/styleguide';
import { isIos } from '@siteground/styleguide/lib/utils/get-device-util/get-device-util';

import { requestLoginPresale, setChatStatus } from '../../app-communication/actions/from-client-frontend';
import { ChatLayout } from '../../components/chat-layout';
import { ChatLogo } from '../../components/chat-logo';
import { CHAT_STATE } from '../../constants';
import { getLanguage } from '../../selectors/chat-session';
import { getFormDataErrors, getOnLoginFailureErrors } from './utils';
import { getPrefillUserData } from '../../selectors/prefill-user-data';

const Login = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { userNames, email: prefilledEmail } = useSelector(getPrefillUserData);

  const [name, setName] = React.useState(userNames);
  const [email, setEmail] = React.useState(prefilledEmail);
  const [question, setQuestion] = React.useState('');

  const [errors, setErrors] = React.useState({
    name: null,
    email: null,
    question: null
  });

  const language = useSelector(getLanguage);
  const chatOpenedFrom = useSelector((store) => store.chat.data);
  const langForRedirect = language ? language.split('_').shift() : 'en';

  const getReferralUrl = () => {
    switch (true) {
      case Boolean(chatOpenedFrom?.campaign):
        return `${window.location.href}?campaign=${chatOpenedFrom?.campaign}`;
      case chatOpenedFrom?.popup:
        return `${window.location.href}?popup`;
      default:
        return window.location.href;
    }
  };

  return (
    <ChatLayout
      header={<ChatLogo />}
      content={
        <Grid gap="medium">
          <Text>
            <FormattedMessage
              id="translate.chat.login.existing.customer.notice"
              values={{
                link: (
                  <Link href={`https://login.siteground.com/login?lang=${langForRedirect}`}>
                    {intl.formatMessage({ id: 'translate.login.existing.customer.notice.link' })}
                  </Link>
                )
              }}
            />
          </Text>

          <Input
            {...{ autoFocus: !isIos() }}
            label={intl.formatMessage({ id: 'translate.login.label.name' })}
            value={name}
            state={errors.name ? 'error' : undefined}
            validationMessage={errors.name && intl.formatMessage({ id: errors.name })}
            onChange={(event) => {
              setName(event.target.value);
              setErrors({
                ...errors,
                name: null
              });
            }}
          />

          <Input
            label={intl.formatMessage({ id: 'translate.login.label.email' })}
            value={email}
            state={errors.email ? 'error' : undefined}
            validationMessage={errors.email && intl.formatMessage({ id: errors.email })}
            onChange={(event) => {
              setEmail(event.target.value);
              setErrors({
                ...errors,
                email: null
              });
            }}
          />

          <Textarea
            rows="3"
            label={intl.formatMessage({ id: 'translate.login.question' })}
            value={question}
            state={errors.question ? 'error' : undefined}
            validationMessage={errors.question && intl.formatMessage({ id: errors.question })}
            onChange={(event) => {
              setQuestion(event.target.value);
              setErrors({
                ...errors,
                question: null
              });
            }}
          />

          <div style={{ textAlign: 'left' }}>
            <Button
              color="primary"
              onClick={() => {
                const formErrors = getFormDataErrors({ name, email, question });
                const hasErrors = Object.values(formErrors).filter(Boolean).length;
                const referring_url = getReferralUrl();

                if (hasErrors) {
                  return setErrors(formErrors);
                }
                dispatch(
                  requestLoginPresale({
                    name,
                    email,
                    question,
                    language,
                    referring_url,
                    onFailure: (response) => {
                      const responseErrors = getOnLoginFailureErrors({ response });
                      const hasErrors = Object.values(responseErrors).filter(Boolean).length;

                      if (hasErrors) {
                        return setErrors(responseErrors);
                      }

                      if (!response.data || !response.data.error) {
                        return;
                      }

                      if (response.data.error.busy) {
                        dispatch(setChatStatus(CHAT_STATE.NO_AVAILABLE_OPERATOR));
                      }

                      if (response.data.error.chatting) {
                        dispatch(setChatStatus(CHAT_STATE.CURRENTLY_CHATTING));
                      }
                    }
                  })
                );
              }}
            >
              {intl.formatMessage({ id: 'translate.login.submit.button' })}
            </Button>
          </div>
          <Text size="small">
            <FormattedMessage
              id="translate.login.privacy.policy.notice"
              values={{
                link: (
                  <Link target="_blank" href={`https://www.siteground.com/privacy.htm?lang=${language}`}>
                    {intl.formatMessage({ id: 'translate.generic.privacy.policy' })}
                  </Link>
                )
              }}
            />
          </Text>

          <Text size="small">
            {intl.formatMessage(
              {
                id: 'translate.recaptcha.terms.description'
              },
              {
                br: () => <br />,
                privacy: (link) => (
                  <Link target="_blank" href={`https://policies.google.com/privacy?hl=${langForRedirect}`}>
                    {' '}
                    {link}{' '}
                  </Link>
                ),
                terms: (text) => (
                  <Link target="_blank" href={`https://policies.google.com/terms?hl=${langForRedirect}`}>
                    {' '}
                    {text}{' '}
                  </Link>
                )
              }
            )}
          </Text>
        </Grid>
      }
    />
  );
};

export default Login;
